import { Layers, MoreHorizontal, Pencil, Plus, Power, PowerOff, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { SearchInput } from "@/components/shared/search-input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  type AdminConstraint,
  useAdminConstraints,
  useConstraintGroups,
  useDeleteConstraint,
  useUpdateConstraint,
} from "@/hooks/use-admin-constraints";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { ConstraintEditorDialog } from "./ConstraintEditorDialog";

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; constraint: AdminConstraint }
  | { type: "delete"; constraint: AdminConstraint };

export function ConstraintsManager() {
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, 200);
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });

  const groupsQuery = useConstraintGroups();
  const constraintsQuery = useAdminConstraints();
  const updateMut = useUpdateConstraint();
  const deleteMut = useDeleteConstraint();

  const groups = groupsQuery.data ?? [];

  const filtered = useMemo(() => {
    const rows = constraintsQuery.data ?? [];
    const term = debounced.trim().toLowerCase();
    return rows.filter((r) => {
      if (groupFilter !== "all" && r.groupId !== groupFilter) return false;
      if (statusFilter === "active" && !r.active) return false;
      if (statusFilter === "inactive" && r.active) return false;
      if (!term) return true;
      return (
        r.name.toLowerCase().includes(term) ||
        r.groupName.toLowerCase().includes(term) ||
        (r.description ?? "").toLowerCase().includes(term)
      );
    });
  }, [constraintsQuery.data, debounced, groupFilter, statusFilter]);

  function closeDialog() {
    setDialog({ type: "none" });
  }

  if (constraintsQuery.isError || groupsQuery.isError) {
    const message =
      constraintsQuery.error?.message ?? groupsQuery.error?.message ?? "Failed to load constraints";
    return (
      <ErrorState
        message={message}
        onRetry={() => {
          constraintsQuery.refetch();
          groupsQuery.refetch();
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3 md:flex-1">
          <SearchInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search constraints..."
            className="md:max-w-xs"
          />
          <Select value={groupFilter} onValueChange={setGroupFilter}>
            <SelectTrigger className="w-full md:w-[180px]">
              <SelectValue placeholder="All groups" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All groups</SelectItem>
              {groups.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
          >
            <SelectTrigger className="w-full md:w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="inactive">Inactive only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setDialog({ type: "create" })} disabled={groups.length === 0}>
          <Plus className="mr-2 h-4 w-4" />
          New Constraint
        </Button>
      </div>

      <DataTable
        headers={[
          { label: "Name" },
          { label: "Group" },
          { label: "Status" },
          { label: "In use", className: "hidden md:table-cell" },
          { label: "Actions", className: "text-right" },
        ]}
        loading={constraintsQuery.isLoading || groupsQuery.isLoading}
        empty={
          <EmptyState
            icon={Layers}
            title="No constraints found"
            description="Adjust the filters above or create a new constraint."
          />
        }
      >
        {filtered.map((c) => (
          <tr
            key={c.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3">
              <div className="font-medium">{c.name}</div>
              {c.description ? (
                <div className="text-xs text-muted-foreground line-clamp-1">{c.description}</div>
              ) : null}
            </td>
            <td className="px-4 py-3">
              <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                {c.groupName}
              </span>
            </td>
            <td className="px-4 py-3">
              <StatusBadge status={c.active ? "completed" : "disabled"}>
                {c.active ? "Active" : "Inactive"}
              </StatusBadge>
            </td>
            <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
              <UsageBreakdown usage={c.usage} />
            </td>
            <td className="px-4 py-3 text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setDialog({ type: "edit", constraint: c })}>
                    <Pencil className="h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => updateMut.mutate({ id: c.id, active: !c.active })}
                  >
                    {c.active ? (
                      <>
                        <PowerOff className="h-4 w-4" />
                        Deactivate
                      </>
                    ) : (
                      <>
                        <Power className="h-4 w-4" />
                        Activate
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => setDialog({ type: "delete", constraint: c })}
                    disabled={c.usage.total > 0}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </td>
          </tr>
        ))}
      </DataTable>

      <ConstraintEditorDialog
        open={dialog.type === "create" || dialog.type === "edit"}
        onOpenChange={(open) => !open && closeDialog()}
        groups={groups}
        constraint={dialog.type === "edit" ? dialog.constraint : undefined}
      />

      {dialog.type === "delete" && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          title="Delete constraint"
          description={`Permanently delete "${dialog.constraint.name}"? This action cannot be undone.`}
          confirmLabel="Delete"
          variant="destructive"
          loading={deleteMut.isPending}
          onConfirm={async () => {
            await new Promise<void>((resolve) => {
              deleteMut.mutate(dialog.constraint.id, {
                onSettled: () => {
                  closeDialog();
                  resolve();
                },
              });
            });
          }}
        />
      )}
    </div>
  );
}

function UsageBreakdown({ usage }: { usage: AdminConstraint["usage"] }) {
  if (usage.total === 0) {
    return <span className="text-xs text-muted-foreground/70">Unused</span>;
  }
  const parts: string[] = [];
  if (usage.solutions > 0)
    parts.push(`${usage.solutions} Solution${usage.solutions === 1 ? "" : "s"}`);
  if (usage.accounts > 0) parts.push(`${usage.accounts} Account${usage.accounts === 1 ? "" : "s"}`);
  if (usage.resourceRatings > 0)
    parts.push(`${usage.resourceRatings} Rating${usage.resourceRatings === 1 ? "" : "s"}`);
  return <span className="text-xs">{parts.join(" · ")}</span>;
}
